/**
 * The twenty-four keys, for the two forms that offer them.
 *
 * ## A list rather than a text box
 *
 * The reason to record a key is so something can later ask "what else is in A
 * minor, or a fifth away from it" - and that question cannot be answered over a
 * column holding `Am`, `A minor`, `A min` and `a-moll`, which is what free text
 * produces within a week. The server refuses anything not on this list
 * (`MusicalKey` in the backend holds the same twenty-four).
 *
 * ## Sharps stored, both spellings shown
 *
 * D♯ and E♭ are one key spelled two ways. Storing both would split it in half
 * and defeat the exercise, so the stored value is always the sharp - and the
 * label shows "D♯ / E♭" so somebody who thinks in flats still finds theirs.
 *
 * The stored form uses a plain ASCII `#`, which is what fits the column and
 * what every other tool writes; the ♯ is only ever on screen.
 */

interface Root {
  /** As stored: `C`, `C#`. Minors get an `m` on the end. */
  value: string;
  label: string;
  /** The same pitch, spelled flat. Only the black notes have one. */
  alt?: string;
}

const ROOTS: Root[] = [
  { value: 'C', label: 'C' },
  { value: 'C#', label: 'C♯', alt: 'D♭' },
  { value: 'D', label: 'D' },
  { value: 'D#', label: 'D♯', alt: 'E♭' },
  { value: 'E', label: 'E' },
  { value: 'F', label: 'F' },
  { value: 'F#', label: 'F♯', alt: 'G♭' },
  { value: 'G', label: 'G' },
  { value: 'G#', label: 'G♯', alt: 'A♭' },
  { value: 'A', label: 'A' },
  { value: 'A#', label: 'A♯', alt: 'B♭' },
  { value: 'B', label: 'B' },
];

const spelt = (root: Root): string => (root.alt === undefined ? root.label : `${root.label} / ${root.alt}`);

/**
 * Majors first, then minors, each chromatic.
 *
 * Not interleaved. Somebody looking for A minor is looking for a minor, and
 * twenty-four rows alternating quality means reading every other line.
 */
export const KEY_OPTIONS: { value: string; label: string }[] = [
  ...ROOTS.map((root) => ({ value: root.value, label: `${spelt(root)} major` })),
  ...ROOTS.map((root) => ({ value: `${root.value}m`, label: `${spelt(root)} minor` })),
];

/**
 * A stored key, written out for reading.
 *
 * Short - "A minor", not "A♯ / B♭ minor" - because this goes beside the BPM on
 * a track page where the enharmonic is noise. The picker is where somebody
 * needs both spellings; a label is where they need one.
 *
 * Anything unrecognised comes back untouched rather than blank: a row written
 * before this list existed should still show whatever it says.
 */
export function describeKey(stored: string): string {
  const minor = stored.endsWith('m');
  const root = minor ? stored.slice(0, -1) : stored;
  const found = ROOTS.find((candidate) => candidate.value === root);

  if (found === undefined) return stored;

  return `${found.label} ${minor ? 'minor' : 'major'}`;
}

/** The bounds the server enforces, repeated so the input can too. */
export const MIN_BPM = 40;
export const MAX_BPM = 300;
